/**
 * Bid mechanics.
 *
 * A listing's rank score is its lifetime total paid, in cents. Raising a bid
 * is a top-up: the company pays only the difference between its current total
 * and the new total it wants to hold. A competitor must beat that absolute
 * total, not the top-up.
 *
 * The rule, whole: bid any amount from $5. Rank is the total paid; passing a
 * listing takes at least a dollar more than it. There is no premium attached
 * to any position — a company may bid any whole-dollar amount that clears the
 * floors and lands wherever that total lands.
 *
 * Only three things make a bid illegal:
 *   - it is not a whole number of dollars;
 *   - it is a first bid below $5;
 *   - it is a raise that adds less than $1 to the listing's running total.
 *
 * Everything else this module computes is descriptive arithmetic, not a rule:
 * "to sit above a row totalling $T you need $T + $1" is just what exceeding a
 * whole-dollar total means. Ties are broken in favour of the earlier bid, so
 * passing a tied row means EXCEEDING its total, not matching it.
 *
 * Posting is not free. A listing's first payment of at least $5 is what
 * publishes it; until then it is a private draft its owner alone can see.
 */
import { CENTS_PER_DOLLAR, assertWholeDollars, isWholeDollars } from './money.js';
/**
 * The smallest amount by which one whole-dollar total can exceed another: $1.
 * This is arithmetic, not a fee — a bid that beats a row beats it by at least
 * a dollar because dollars are the only denomination there is.
 */
export const PASS_INCREMENT_CENTS = 100;
/**
 * A listing's first paid bid must be at least $5.
 *
 * This is also the price of publishing: a listing is not visible anywhere on
 * the public site until a payment of at least this much has completed, so the
 * opening floor and the cost of admission are the same number.
 */
export const MINIMUM_FIRST_BID_CENTS = 500;
/** Any subsequent raise must add at least $1 to the running total. */
export const MINIMUM_RAISE_INCREMENT_CENTS = 100;
/**
 * The smallest total that sits above the listing whose lifetime total is
 * `rowTotalCents`: that row's total plus a dollar.
 */
export function nextMinimumCents(rowTotalCents) {
    assertWholeDollars(rowTotalCents, 'row total');
    return rowTotalCents + PASS_INCREMENT_CENTS;
}
/**
 * The smallest new lifetime total a listing already holding
 * `currentTotalCents` may raise to: its current total plus $1.
 */
export function minimumRaiseCents(currentTotalCents) {
    assertWholeDollars(currentTotalCents, 'current total');
    return currentTotalCents + MINIMUM_RAISE_INCREMENT_CENTS;
}
/**
 * The smallest lifetime total this listing may legally hold after its next
 * payment. An unpublished listing must clear the $5 opening floor; a published
 * one must add at least $1 to what it already holds. The board it is bidding
 * into does not enter into it — no position carries a price of its own.
 */
export function minimumNextTotalCents(currentTotalCents) {
    assertWholeDollars(currentTotalCents, 'current total');
    return currentTotalCents === 0
        ? MINIMUM_FIRST_BID_CENTS
        : minimumRaiseCents(currentTotalCents);
}
/**
 * What the company actually pays to move from its current lifetime total to
 * `desiredTotalCents` — the delta, never the whole new total.
 */
export function topUpCents(currentTotalCents, desiredTotalCents) {
    assertWholeDollars(currentTotalCents, 'current total');
    assertWholeDollars(desiredTotalCents, 'desired total');
    if (desiredTotalCents <= currentTotalCents) {
        throw new RangeError('desired total must exceed the current total');
    }
    return desiredTotalCents - currentTotalCents;
}
/**
 * The rank a listing would hold at `bidTotalCents`, as previewed live while
 * the company types. Rank is 1 + the number of listings already at or above
 * that total, which encodes the tie rule: matching a total does not take the
 * position, because the earlier bid keeps it.
 *
 * `totalsCents` is every competing listing's lifetime total, in any order.
 * The bidding listing's own current total must not appear in it.
 */
export function claimRankForAmount(bidTotalCents, totalsCents) {
    let atOrAbove = 0;
    for (const total of totalsCents) {
        if (total >= bidTotalCents)
            atOrAbove += 1;
    }
    return atOrAbove + 1;
}
/**
 * The lifetime total required to hold `targetRank` OR BETTER against
 * `totalsCents`: a dollar over the listing currently sitting there, never
 * below the opening floor. A target beyond the end of the board costs the
 * opening floor.
 *
 * On a tied board a dollar over a tied row clears the whole tie block, so the
 * figure returned can land better than the rank asked for. The invariant is
 * `claimRankForAmount(costToHoldRankCents(totals, r), totals) <= r`.
 */
export function costToHoldRankCents(totalsCents, targetRank) {
    if (!Number.isInteger(targetRank) || targetRank < 1) {
        throw new RangeError('target rank must be a positive integer');
    }
    const descending = [...totalsCents].sort((a, b) => b - a);
    if (descending.length === 0)
        return MINIMUM_FIRST_BID_CENTS;
    const occupant = descending[targetRank - 1];
    if (occupant === undefined)
        return MINIMUM_FIRST_BID_CENTS;
    return Math.max(nextMinimumCents(occupant), MINIMUM_FIRST_BID_CENTS);
}
/**
 * The lifetime total that takes first place on a board: a dollar over the
 * leader's total, never below the opening floor. An empty board is led by the
 * $5 first bid alone.
 *
 * This is what every "top spot costs" figure on the site quotes. It is a
 * description of where the money would land, not a toll — a company is free to
 * bid less and sit lower, or more and sit further clear.
 */
export function topSpotTotalCents(competingTotalsCents) {
    const leader = competingTotalsCents.reduce((highest, total) => Math.max(highest, total), 0);
    return Math.max(nextMinimumCents(leader), MINIMUM_FIRST_BID_CENTS);
}
/**
 * Validate a proposed new lifetime total against the listing's current total,
 * returning either the amount to charge or a customer-facing reason.
 *
 * This is the whole of the law, and both the live preview and the checkout
 * handler call it so the two can never disagree: whole dollars, a $5 floor on
 * the bid that publishes, and at least $1 added by every raise. No position on
 * the board is refused to anyone who clears those floors.
 */
export function validateBid(currentTotalCents, desiredTotalCents) {
    const minimumTotalCents = minimumNextTotalCents(currentTotalCents);
    if (!isWholeDollars(desiredTotalCents)) {
        return {
            ok: false,
            code: 'NOT_WHOLE_DOLLARS',
            message: 'Bids are placed in whole dollars.',
            minimumTotalCents,
        };
    }
    if (desiredTotalCents < minimumTotalCents) {
        const isFirstBid = currentTotalCents === 0;
        return {
            ok: false,
            code: isFirstBid ? 'BELOW_FIRST_BID_MINIMUM' : 'BELOW_RAISE_MINIMUM',
            message: isFirstBid
                ? `A first bid must be at least $${MINIMUM_FIRST_BID_CENTS / CENTS_PER_DOLLAR}.`
                : 'A raise must add at least $1 to your current total.',
            minimumTotalCents,
        };
    }
    return {
        ok: true,
        totalCents: desiredTotalCents,
        chargeCents: desiredTotalCents - currentTotalCents,
    };
}
//# sourceMappingURL=bid-math.js.map